<template>
  <div v-if="isAdminUser || hasMultiCompanyAccess" class="rf-detail-content-wrapper mt-4">
    <!-- Company Switcher for users with multi-company access -->
    <v-card v-if="hasMultiCompanyAccess && !isAdminUser" class="mb-4 p-0!">
      <v-card-title class="bg-[#082638] text-white">
        <v-icon start>fa:fas fa-building</v-icon>
        Switch Company Context
      </v-card-title>
      <v-card-text class="pa-6">
        <p class="mb-4 text-sm text-gray-600">Select a company to view and manage its data:</p>
        <v-autocomplete
          v-model="currentCompany"
          hideDetails
          variant="outlined"
          density="compact"
          label="Select Company"
          persistentPlaceholder
          :items="formattedAccessibleCompanies"
          clearable
          returnObject
          itemTitle="displayName"
          itemValue="slug"
          :autoSelectFirst="false"
          @update:modelValue="onCompanyChange"
        >
        </v-autocomplete>
      </v-card-text>
    </v-card>

    <v-card v-if="isAdminUser" class="p-0!">
      <v-tabs v-model="companySettingsTab" alignTabs="center" bgColor="#082638" height="70" dark>
        <v-tab v-if="userStore.isCurrentSuperAdmin" value="tab-1">
          <v-icon start>fa:fas fa-building</v-icon>
          <span class="v-tab--text"> Companies </span>
        </v-tab>
        <v-tab v-if="isAdminUser" value="tab-2">
          <v-icon start>fa:fas fa-users</v-icon>
          <span class="v-tab--text"> Users </span>
        </v-tab>
      </v-tabs>
      <v-tabs-window v-model="companySettingsTab">
        <v-window-item v-if="userStore.isCurrentSuperAdmin" value="tab-1">
          <v-card flat class="p-6!">
            <div class="settings-add-new-company">
              <h3 class="title">New Company</h3>
              <form @submit.prevent="onSubmitCompany">
                <v-text-field
                  v-model="company.name"
                  label="Company Name"
                  variant="outlined"
                  density="compact"
                  hideDetails
                  persistentPlaceholder
                  bgColor="#ffffff"
                  style="margin-bottom: 0.75rem"
                ></v-text-field>
                <v-text-field
                  v-model="company.awsMarketplaceCustomerId"
                  label="Aws Marketplace Customer ID"
                  variant="outlined"
                  density="compact"
                  hideDetails
                  persistentPlaceholder
                  bgColor="#ffffff"
                  style="margin-bottom: 0.75rem"
                ></v-text-field>
                <v-checkbox
                  v-model="company.awsMarketplaceEnabled"
                  label="Aws Marketplace customer?"
                  density="compact"
                  hideDetails
                  style="margin-bottom: 0.75rem"
                ></v-checkbox>
                <v-btn
                  variant="flat"
                  color="success"
                  type="submit"
                  class="md-success"
                  :loading="isSubmittingCompany"
                  :disabled="isSubmittingCompany"
                >
                  Submit
                </v-btn>
              </form>
            </div>
            <v-table class="experiments-results-table">
              <thead>
                <tr>
                  <th class="col-exp-1">Company Name</th>
                  <th class="text-left">Timezone Offset</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in companiesStore.companies" :key="index">
                  <td style="width: 40%">
                    {{ item.name }}
                  </td>
                  <td>{{ item.timezone_offset ? item.timezone_offset : "N/A" }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
        </v-window-item>
        <v-window-item v-if="isAdminUser" value="tab-2">
          <v-card flat style="padding: 1.5rem">
            <div v-if="hasMultiCompanyAccess" style="width: 400px; margin: 0 auto 1.5rem auto">
              <v-autocomplete
                v-model="currentCompany"
                hideDetails
                variant="outlined"
                density="compact"
                label="Select Company"
                persistentPlaceholder
                :items="formattedAccessibleCompanies"
                clearable
                returnObject
                itemTitle="displayName"
                itemValue="slug"
                :autoSelectFirst="false"
                @update:modelValue="onCompanyChange"
              >
              </v-autocomplete>
            </div>
            <RfAlert v-else type="warning" class="mx-auto mb-4 max-w-xl">
              To request access to other companies, please reach out to IT.
            </RfAlert>
            <RfManageUsers :currentCompany="currentCompany" />
          </v-card>
        </v-window-item>
      </v-tabs-window>
    </v-card>
  </div>
</template>

<script>
import RfManageUsers from "@/components/RfSettings/RfManageUsers.vue";
import { useCompaniesStore } from "@/pinia/companiesStore";
import { useUserStore } from "@/pinia/userStore";
import { useToastsStore } from "@/pinia/toastsStore";
import StringUtils from "@/utils/StringUtils";
import RfAlert from "@/components/alerts/RfAlert.vue";

export default {
  name: "RfSettingsCompany",
  components: { RfManageUsers, RfAlert },
  setup: () => ({
    companiesStore: useCompaniesStore(),
    userStore: useUserStore(),
    toastsStore: useToastsStore(),
  }),
  data: () => ({
    companySettingsTab: null,
    currentCompany: null,
    company: { name: "", awsMarketplaceCustomerId: "", awsMarketplaceEnabled: false },
    isSubmittingCompany: false,
  }),
  computed: {
    isAdminUser() {
      return this.userStore.isCurrentSuperAdmin || this.userStore.isCurrentAdmin;
    },
    hasMultiCompanyAccess() {
      // User has multi-company access flag from backend
      return this.userStore.currUser?.has_multi_company_access || false;
    },
    formattedAccessibleCompanies() {
      // Format companies to display "Company Name (role)"
      return (this.companiesStore.accessibleCompanies || []).map(company => ({
        ...company,
        displayName: company.acting_role
          ? `${company.name} (${StringUtils.titleize(company.acting_role)})`
          : company.name,
      }));
    },
  },
  watch: {
    "userStore.currUser.id": {
      handler(newUserId) {
        if (newUserId && this.hasMultiCompanyAccess) {
          this.companiesStore.getAccessibleCompanies(newUserId);
        }
      },
      immediate: true,
    },
  },
  methods: {
    onCompanyChange(newCompany) {
      // Handle company selection change
      if (newCompany?.slug) {
        // Only set viewing context if it's a DIFFERENT company than user's own
        // Note: Backend returns slug in company_id field
        if (newCompany.slug === this.userStore.currUser?.company_id) {
          // Selected their own company - clear the context
          this.companiesStore.clearViewingCompany();
        } else {
          // Selected a different company - set the context with slug and role
          this.companiesStore.setViewingCompany({
            slug: newCompany.slug,
            name: newCompany.name,
            role: newCompany.acting_role,
          });
        }
      } else {
        // Cleared the selection - remove context
        this.companiesStore.clearViewingCompany();
      }

      // Redirect to root to load apps from new company context
      window.location.href = "/";
    },
    async onSubmitCompany() {
      if (this.company.name.length > 2) {
        this.isSubmittingCompany = true;
        const params = {
          companyName: this.company.name,
          awsMarketplaceCustomerId: this.company.awsMarketplaceCustomerId,
          awsMarketplaceEnabled: this.company.awsMarketplaceEnabled,
        };
        try {
          await this.companiesStore.createCompany(params);
          this.company.name = "";
          this.company.awsMarketplaceCustomerId = "";
          this.company.awsMarketplaceEnabled = false;
          this.toastsStore.create({ type: "success", body: "Company created successfully" });
        } catch (error) {
          this.toastsStore.create({
            type: "error",
            body: error.message || "Failed to create company",
          });
        } finally {
          this.isSubmittingCompany = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.company-user-role {
  display: flex;
  flex-flow: row;
  padding-top: 1rem;
}
.company-user-role .v-input--radio-group {
  margin: 0 0 0 10px;
}
.settings-add-new-company {
  max-width: 400px;
  padding: 1rem;
  background: #eee;
  margin: 0 0 1rem 1.25rem;
}
.settings-add-new-company h3 {
  padding: 0;
  margin: 0 0 0.75rem;
  line-height: normal;
}
</style>
